/**
 * cart-shoe-care.js
 * Shoe care carousel: arrow navigation, dot indicators, add-to-cart via Shopify AJAX API.
 * Initialises on DOMContentLoaded and re-initialises after cart drawer re-renders.
 */

(function () {
  'use strict';

  // -------------------------------------------------------
  // Carousel logic
  // -------------------------------------------------------

  function initShoeCareCarousel(container) {
    const track      = container.querySelector('[data-shoe-care-track]');
    const cards      = Array.from(container.querySelectorAll('[data-shoe-care-card]'));
    const prevBtn    = container.querySelector('[data-shoe-care-prev]');
    const nextBtn    = container.querySelector('[data-shoe-care-next]');
    const dotsWrap   = container.querySelector('[data-shoe-care-dots]');

    if (!track || cards.length === 0) return;

    // Determine how many cards are fully visible at once
    function getVisibleCount() {
      const trackWidth = track.clientWidth;
      const cardWidth  = cards[0].offsetWidth + parseInt(getComputedStyle(track).gap || 0, 10);
      return Math.max(1, Math.floor(trackWidth / cardWidth));
    }

    // ---- Dots ----
    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      const pages = Math.ceil(cards.length / getVisibleCount());

      if (pages <= 1) {
        dotsWrap.classList.add('cart-shoe-care__dots--single-page');
        return;
      }
      dotsWrap.classList.remove('cart-shoe-care__dots--single-page');

      for (let i = 0; i < pages; i++) {
        const dot = document.createElement('button');
        dot.className = 'cart-shoe-care__dot';
        dot.setAttribute('type', 'button');
        dot.setAttribute('aria-label', 'Go to page ' + (i + 1));
        dot.addEventListener('click', () => scrollToPage(i));
        dotsWrap.appendChild(dot);
      }
    }

    function updateDots() {
      if (!dotsWrap) return;
      const dots       = dotsWrap.querySelectorAll('.cart-shoe-care__dot');
      if (!dots.length) return;
      const visCount   = getVisibleCount();
      const scrollLeft = track.scrollLeft;
      const cardWidth  = cards[0].offsetWidth + parseInt(getComputedStyle(track).gap || 0, 10);
      const page       = Math.round(scrollLeft / (cardWidth * visCount));

      dots.forEach((d, i) => d.classList.toggle('is-active', i === page));
    }

    function scrollToPage(page) {
      const visCount  = getVisibleCount();
      const cardWidth = cards[0].offsetWidth + parseInt(getComputedStyle(track).gap || 0, 10);
      track.scrollTo({ left: cardWidth * visCount * page, behavior: 'smooth' });
    }

    // ---- Arrow state ----
    function updateArrows() {
      if (!prevBtn || !nextBtn) return;
      const atStart = track.scrollLeft <= 4;
      const atEnd   = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;
    }

    // ---- Scroll by one card-width ----
    function scrollBy(dir) {
      const cardWidth = cards[0].offsetWidth + parseInt(getComputedStyle(track).gap || 0, 10);
      track.scrollBy({ left: dir * cardWidth, behavior: 'smooth' });
    }

    // ---- Events ----
    if (prevBtn) prevBtn.addEventListener('click', () => scrollBy(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => scrollBy(1));

    track.addEventListener('scroll', () => {
      updateArrows();
      updateDots();
    }, { passive: true });

    // Rebuild dots on resize
    const resizeObserver = new ResizeObserver(() => {
      buildDots();
      updateArrows();
      updateDots();
    });
    resizeObserver.observe(track);

    // ---- Click-drag to scroll (desktop mouse users) ----
    let isDragging = false;
    let dragStartX = 0;
    let dragScrollLeft = 0;

    track.addEventListener('mousedown', (e) => {
      isDragging = true;
      dragStartX = e.pageX - track.offsetLeft;
      dragScrollLeft = track.scrollLeft;
      track.classList.add('is-dragging');
    });

    const stopDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      track.classList.remove('is-dragging');
      // Snap to nearest card after releasing
      const cardWidth = cards[0].offsetWidth + parseInt(getComputedStyle(track).gap || 0, 10);
      const page = Math.round(track.scrollLeft / cardWidth);
      track.style.scrollBehavior = 'smooth';
      track.scrollTo({ left: page * cardWidth, behavior: 'smooth' });
    };

    track.addEventListener('mouseleave', stopDrag);
    track.addEventListener('mouseup', stopDrag);

    track.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x    = e.pageX - track.offsetLeft;
      const walk = (x - dragStartX) * 1.5; // multiplier for feel
      track.scrollLeft = dragScrollLeft - walk;
    });

    buildDots();
    updateArrows();
    updateDots();
  }

  // -------------------------------------------------------
  // Add-to-cart via Shopify AJAX API
  // -------------------------------------------------------

  function attachAddToCartListeners(container) {
    const buttons = container.querySelectorAll('[data-shoe-care-add]');

    buttons.forEach((btn) => {
      // Always re-wire — DOM is replaced after each cart update so no duplicate risk

      btn.addEventListener('click', async function () {
        const variantId = this.dataset.variantId;
        if (!variantId || this.disabled) return;

        setButtonState(this, 'loading');

        try {
          const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });

          if (!response.ok) {
            throw new Error('Add to cart failed');
          }

          setButtonState(this, 'added');

          // ALCMY tracking: report the successful add to GA4 via the custom pixel
          trackShoeCareAdd(this);

          // Trigger the cart drawer to re-render — works with Shopify's
          // PubSub event system used in Dawn-based themes.
          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shoe-care' });
          }

          // Also fire a custom event so any cart drawer implementation can listen
          document.dispatchEvent(new CustomEvent('shoe-care:added', { detail: { variantId } }));

          // Refresh the cart drawer sections if the CartDrawer class exposes renderContents
          await refreshCartDrawer();

          // After 2.5 s reset button (product may already be in cart on next open)
          setTimeout(() => {
            setButtonState(this, 'idle');
          }, 2500);

        } catch (err) {
          console.error('[ShoeCare] Add to cart error:', err);
          setButtonState(this, 'idle');
        }
      });
    });
  }

  // -------------------------------------------------------
  // ALCMY tracking
  // Publishes a Shopify custom event after a successful add.
  // The "ALCMY GA4 custom events" pixel (Settings > Customer
  // events) forwards events flagged _alcmy to GA4.
  // -------------------------------------------------------

  function trackShoeCareAdd(btn) {
    try {
      if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') return;

      const wrapper = btn.closest('#CartShoeCare');
      const price   = parseFloat(btn.dataset.price);
      const params  = {
        _alcmy: true,
        item: btn.dataset.productHandle || '',
        item_name: btn.dataset.productTitle || '',
        variant_id: btn.dataset.variantId || '',
        list_position: btn.dataset.listPosition || '',
        page_type: wrapper ? (wrapper.dataset.pageType || '') : ''
      };

      if (!isNaN(price)) {
        params.value = price;
        params.currency = (window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
      }

      window.Shopify.analytics.publish('cart_carousel_add', params);
    } catch (e) {
      // Tracking must never break add to cart
    }
  }

  function setButtonState(btn, state) {
    btn.classList.remove('is-loading', 'is-added');
    if (state === 'loading') btn.classList.add('is-loading');
    if (state === 'added')   btn.classList.add('is-added');
    btn.disabled = (state === 'loading');
  }

  // -------------------------------------------------------
  // Refresh cart drawer without closing it
  // -------------------------------------------------------

  async function refreshCartDrawer() {
    try {
      // Dawn / Horsforth uses section rendering — fetch the cart-drawer section
      // and update just the items and icon bubble.
      const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
      const url = `${window.location.pathname}?sections=${sectionIds.join(',')}`;

      const resp = await fetch(url);
      if (!resp.ok) return;

      const data = await resp.json();

      // Update cart items area
      const drawerEl = document.getElementById('CartDrawer');
      if (drawerEl && data['cart-drawer']) {
        const parser = new DOMParser();
        const parsed = parser.parseFromString(data['cart-drawer'], 'text/html');
        const newInner = parsed.querySelector('.drawer__inner');
        const oldInner = drawerEl.querySelector('.drawer__inner');
        if (newInner && oldInner) {
          oldInner.innerHTML = newInner.innerHTML;
          // Re-init shoe care inside the freshly rendered inner
          initAll();
        }
      }

      // Update cart icon bubble count
      const iconBubble = document.getElementById('cart-icon-bubble');
      if (iconBubble && data['cart-icon-bubble']) {
        const parser = new DOMParser();
        const parsed = parser.parseFromString(data['cart-icon-bubble'], 'text/html');
        const newBubble = parsed.querySelector('.shopify-section');
        if (newBubble) iconBubble.innerHTML = newBubble.innerHTML;
      }
    } catch (e) {
      // Non-critical — drawer will update on next open
    }
  }

  // -------------------------------------------------------
  // Init everything inside a given root (default: document)
  // -------------------------------------------------------

  function initAll(root) {
    const scope = root || document;
    scope.querySelectorAll('#CartShoeCare').forEach((container) => {
      initShoeCareCarousel(container);
      attachAddToCartListeners(container);
    });
  }

  // -------------------------------------------------------
  // Bootstrap
  // -------------------------------------------------------

  document.addEventListener('DOMContentLoaded', () => initAll());

  // Re-init when cart drawer renders fresh HTML (e.g. after a standard add-to-cart)
  document.addEventListener('cart-drawer:rendered', () => initAll());

  // MutationObserver — subtree:true catches deep re-renders after add/remove
  const drawerEl = document.getElementById('CartDrawer');
  if (drawerEl) {
    let reinitTimer;
    const observer = new MutationObserver(() => {
      // Debounce — the DOM may fire multiple mutations during a single re-render
      clearTimeout(reinitTimer);
      reinitTimer = setTimeout(() => initAll(), 50);
    });
    observer.observe(drawerEl, { childList: true, subtree: true });
  }

})();